/**
 * The persistent cart bar.
 *
 * Pinned above the safe area, outside the scroll view, so the running total and
 * the route to checkout are always one tap away — the single most important
 * ergonomic decision in the customer app.
 */

import { formatMoney } from '@yummy/shared';
import { Button, Row, Stack, Text, useT } from '@yummy/ui-rnr';

import { FooterBar } from './FooterBar';

/**
 * Shows the draft order total and the checkout action.
 *
 * @param props Item count, subtotal, currency, and the review handler.
 * @returns The pinned cart bar, or nothing while the cart is empty.
 */
export function CartBar({
  itemCount,
  subtotalCents,
  currency,
  onReview,
  disabled,
  hint,
}: {
  itemCount: number;
  subtotalCents: number;
  currency: string;
  onReview: () => void;
  disabled?: boolean;
  hint?: string;
}) {
  const t = useT();

  if (itemCount === 0) return null;

  const reviewLabel = t('customer.cart.review');

  return (
    <FooterBar>
      <Stack gap="sm">
        <Row spread gap="md" align="center">
          <Stack gap="xs">
            <Text className="text-muted-foreground text-sm">
              {t('common.itemCount', { count: itemCount })}
            </Text>
            <Text className="text-lg font-bold">{formatMoney(subtotalCents, currency)}</Text>
          </Stack>
          <Button
            onPress={onReview}
            disabled={disabled}
            size="lg"
            accessibilityLabel={reviewLabel}
            className="min-w-[180px]">
            <Text>{reviewLabel}</Text>
          </Button>
        </Row>
        {hint ? (
          <Text className="text-subtle-foreground text-center text-xs">{hint}</Text>
        ) : null}
      </Stack>
    </FooterBar>
  );
}
